import { axiosInstance } from '@/boot/axios';
import { useGeneralStore } from '@/store/generalStore';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { set } from 'lodash-es';

async function axiosRetryRequest<T>({
  method,
  amountOfRetries = 3,
  path,
  data,
  config,
}: {
  method: 'get' | 'post' | 'put' | 'patch' | 'delete';
  amountOfRetries?: number;
  path: string;
  data?: unknown;
  config?: AxiosRequestConfig;
}): Promise<AxiosResponse<T>> {
  const args: [string, unknown?, AxiosRequestConfig?] = [
    path,
    data,
    config,
  ].filter((i) => !!i) as [string, unknown?, AxiosRequestConfig?];
  for (let i = 0; i < amountOfRetries; i++) {
    try {
      const response = await axiosInstance[method]<T>(...args);
      if (response.headers['x-devstride-correlation-id'] && response.data) {
        set(
          response,
          'data.devstrideCorrelationId',
          response.headers['x-devstride-correlation-id']
        );
      }
      return response;
    } catch (e) {
      const error = e as {
        response?: { status?: number; data?: { maintenance?: boolean } };
      };
      if (
        error.response &&
        error.response.status === 503 &&
        error.response.data?.maintenance === true
      ) {
        const generalStore = useGeneralStore();
        await generalStore.setMaintenanceMode(true);
        break;
      }
      if (
        !error.response ||
        (error.response.status && error.response.status < 500) ||
        i === amountOfRetries - 1
      ) {
        throw e;
      }
    }
  }

  throw new Error(`Failed retrying ${amountOfRetries} times`);
}

export class BaseModule {
  basePath: string;

  constructor(basePath: string) {
    if (basePath.substr(-1) === '/') {
      this.basePath = basePath.substr(0, basePath.length - 1);
    } else {
      this.basePath = basePath;
    }
  }

  protected get<T>(
    path?: string,
    config?: AxiosRequestConfig,
    amountOfRetries?: number
  ) {
    path = path ? `/${path}` : '';
    return axiosRetryRequest<T>({
      method: 'get',
      path: `${this.basePath}${path}`,
      config,
      amountOfRetries,
    });
  }

  protected delete<T>(
    path?: string,
    config?: AxiosRequestConfig,
    amountOfRetries?: number
  ) {
    path = path ? `/${path}` : '';
    return axiosRetryRequest<T>({
      method: 'delete',
      path: `${this.basePath}${path}`,
      config,
      amountOfRetries,
    });
  }

  protected post<T>(
    path?: string,
    data?: unknown,
    config?: AxiosRequestConfig,
    amountOfRetries?: number
  ) {
    path = path ? `/${path}` : '';
    return axiosRetryRequest<T>({
      method: 'post',
      path: `${this.basePath}${path}`,
      data,
      config,
      amountOfRetries,
    });
  }

  protected put<T>(
    path?: string,
    data?: unknown,
    config?: AxiosRequestConfig,
    amountOfRetries?: number
  ) {
    path = path ? `/${path}` : '';
    return axiosRetryRequest<T>({
      method: 'put',
      path: `${this.basePath}${path}`,
      data,
      config,
      amountOfRetries,
    });
  }

  protected patch<T>(
    path?: string,
    data?: unknown,
    config?: AxiosRequestConfig,
    amountOfRetries?: number
  ) {
    path = path ? `/${path}` : '';
    return axiosRetryRequest<T>({
      method: 'patch',
      path: `${this.basePath}${path}`,
      data,
      config,
      amountOfRetries,
    });
  }
}
